import {
  Action,
  ActionReducerMapBuilder,
  createSlice,
  PayloadAction,
  SliceCaseReducers,
} from "@reduxjs/toolkit";
import { getChecklistStatus, isEmpty, toDateString } from "helpers";
import { fromLocal } from "helpers/fromLocal";
import technicianSettings from "helpers/technicianSettings";
import localForage from "localforage";
import { cloneDeep } from "lodash";
import { Extra } from "models/extra";
import { Job, Jobs } from "models/Job";
import { JobVisitType, PartnerJobVisitsStorageKey } from "models/JobVisitType";
import { Times } from "models/travelTimes";
import { UsedPart } from "models/usedPart";
import { TechnicianSignoffValidationSchema } from "models/validation/TechnicianSignoffValidationSchema";
import { VisitValidationSchema } from "models/validation/VisitValidationSchema";
import {
  ChecklistType,
  FileType,
  JobStatus,
  MeterReadingInputType,
  PartStatus,
  QuestionDataType,
  StockStore,
  UserConsentType,
  WorkNoteType,
} from "operations/schema/schema";
import { RootState } from "store";
import { ValidationError } from "yup";
import { asyncMutations, mutationBuilder } from "./partner.mutations";
import { asyncQueries, queryBuilder } from "./partner.queries";

// Interface for state
export interface State {
  token: string | null;
  partnerBasicConsent: UserConsentType | null;
  selectedJobId: string | undefined;
  jobs: Jobs;
  jobVisits: {
    [x: string]: JobVisitType;
  };
  jobTab: string;
  lastLoaded: string | null;
  loadingJob?: boolean;
  refreshJobNotifications: {
    symptomText: boolean;
    internalNotes: boolean;
    files: boolean;
    preOrderedParts: boolean;
  };
  changeJobMachine: {
    loading: boolean;
    open: boolean;
  };
  editMachineLoading: boolean;
  contactsLoading: boolean;
  fileUploading: boolean;
  commentsLoading: boolean;
}

// Interface for store reducers
interface Reducers extends SliceCaseReducers<State> {
  partnerInit: (state: State) => State;
  setPartnerToken: (state: State, action: PayloadAction<{ token: string }>) => State;
  updatePartnerBasicConsent: (
    state: State,
    action: PayloadAction<{ consent: UserConsentType }>,
  ) => State;
  setJobTab: (state: State, action: PayloadAction<{ tab: string }>) => State;
  resetJobStatus: (state: State, action: PayloadAction<{ jobId: string }>) => State;
  setJobStatus: (
    state: State,
    action: PayloadAction<{ jobId: string; status: JobStatus }>,
  ) => State;
  setChangeJobMachineOpen: (state: State, action: PayloadAction<{ open: boolean }>) => State;
  setVisitCompleted: (state: State, action: Action) => State;
  setTravelTimes: (state: State, action: PayloadAction<{ times: Times[] }>) => State;
  setAutoEndTime: (state: State, action: PayloadAction<{ autoEndTime: boolean }>) => State;
  setWorkTimesInvoiceType: (
    state: State,
    action: PayloadAction<{ invoiceType: string | null }>,
  ) => State;
  setTravelTimesInvoiceType: (
    state: State,
    action: PayloadAction<{ invoiceType: string | null }>,
  ) => State;
  // Form
  setVisitValue: <K extends Extract<keyof JobVisitType, string>>(
    state: State,
    action: PayloadAction<{ key: K; value: JobVisitType[K]; shouldValidate?: boolean }>,
  ) => State;
  validateVisit: (state: State, action: Action) => State;
  // Checklists
  updateChecklist: (
    state: State,
    action: PayloadAction<{ checklist: ChecklistType; index: number }>,
  ) => State;
  // Files
  addFile: (state: State, action: PayloadAction<{ file: FileType }>) => State;
  removeFile: (state: State, action: PayloadAction<{ id: string }>) => State;
  updateFile: (state: State, action: PayloadAction<{ file: FileType }>) => State;
  // MeterReadings
  updateMeterReadings: (
    state: State,
    action: PayloadAction<{ readings: MeterReadingInputType[] }>,
  ) => State;
  setMetersNotSaved: (state: State, action: PayloadAction<{ unSavedChanges: boolean }>) => State;
  setMetersUseCurrent: (state: State, action: PayloadAction<{ useCurrent: boolean }>) => State;
  // Extras
  addExtra: (state: State, action: PayloadAction<{ extra: Extra }>) => State;
  removeExtra: (state: State, action: PayloadAction<{ index: number }>) => State;
  updateExtra: (state: State, action: PayloadAction<{ extra: Extra; index: number }>) => State;
  // Parts
  addPart: (state: State, action: PayloadAction<{ part: UsedPart }>) => State;
  addParts: (state: State, action: PayloadAction<{ parts: UsedPart[] }>) => State;
  removePart: (
    state: State,
    action: PayloadAction<{ articleNo: string; stockStore: StockStore }>,
  ) => State;
  updatePart: (state: State, action: PayloadAction<{ part: UsedPart }>) => State;
  // Signature
  setSignatureCustomer: (
    state: State,
    action: PayloadAction<{ signatureData: string | undefined }>,
  ) => State;
  setSignatureTechnician: (
    state: State,
    action: PayloadAction<{ signatureData: string | undefined }>,
  ) => State;
  addTravel: (state: State) => State;
  stopTravel: (state: State) => State;
  resetRefreshNotifications: (
    state: State,
    action: PayloadAction<{ key: string | undefined }>,
  ) => State;
}

// Interface for store selectors (if necessary)
interface Selectors {
  selectPartnerBasicConsent: (state: RootState) => UserConsentType | null;
  selectJobLoaded: (state: RootState) => boolean;
  selectVisitLoaded: (state: RootState) => boolean;
  selectTravelTimes: (state: RootState) => Times[];
  selectSelectedJob: (state: RootState) => Job;
  selectSelectedJobVisit: (state: RootState) => JobVisitType;
  selectWorkNotes: (state: RootState) => WorkNoteType[];
  selectTravelTabInvalid: (state: RootState) => boolean;
  selectChecklistsIncomplete: (state: RootState) => boolean;
  selectVisitCanComplete: (state: RootState) => boolean;
  selectIsJobInProgress: (state: RootState) => boolean;
  selectVisitStarted: (state: RootState) => boolean;
  selectContactsLoading: (state: RootState) => boolean;
}

// Definition of actual (initial) state
export const initialState: State = {
  token: null,
  partnerBasicConsent: null,
  selectedJobId: undefined,
  jobs: {},
  jobVisits: {},
  jobTab: "details",
  lastLoaded: null,
  refreshJobNotifications: {
    symptomText: false,
    internalNotes: false,
    files: false,
    preOrderedParts: false,
  },
  changeJobMachine: {
    loading: false,
    open: false,
  },
  editMachineLoading: false,
  contactsLoading: false,
  fileUploading: false,
  commentsLoading: false,
};

export const storePartnerVisitsToLocal = (state: State) => {
  const copiedVisits = JSON.parse(JSON.stringify(state.jobVisits));
  for (const key in copiedVisits) {
    //copiedVisits[key].files = [];
    if (!technicianSettings.storeSignaturesLocal) {
      copiedVisits[key].signatureCustomer = undefined;
      copiedVisits[key].signatureEngineer = undefined;
    }
  }
  try {
    localForage.setItem(PartnerJobVisitsStorageKey, copiedVisits);
  } catch {
    return;
  }
};

// Definition of actual reducers
const reducers: Reducers = {
  partnerInit(state) {
    state.partnerBasicConsent = fromLocal<UserConsentType>("PartnerBasicConsent") || null;
    return state;
  },
  setPartnerToken(state, { payload: { token } }) {
    state.token = token;
    return state;
  },
  updatePartnerBasicConsent(state, { payload: { consent } }) {
    state.partnerBasicConsent = consent;
    localStorage.setItem("PartnerBasicConsent", JSON.stringify(consent));
    return state;
  },
  setJobTab(state, { payload: { tab } }) {
    state.jobTab = tab;
    return state;
  },
  setChangeJobMachineOpen: (state, { payload: { open } }) => {
    state.changeJobMachine.open = open;
    return state;
  },
  setJobStatus: (state, { payload: { jobId, status } }) => {
    if (!state.jobs[jobId] || state.jobs[jobId].status === status) return state;
    state.jobs[jobId].previousStatus = state.jobs[jobId].status || undefined;
    state.jobs[jobId].status = status;
    return state;
  },
  resetJobStatus: (state, { payload: { jobId } }) => {
    if (!state.jobs[jobId] || !state.jobs[jobId].previousStatus) return state;
    state.jobs[jobId].status = state.jobs[jobId].previousStatus;
    state.jobs[jobId].previousStatus = undefined;
    return state;
  },
  setVisitCompleted: (state) => {
    const { selectedJobId, jobVisits } = state;
    if (!selectedJobId || !jobVisits[selectedJobId]) {
      throw new Error("JobVisit not initialized or unavailable. Should not be possible to trigger");
    }
    delete jobVisits[selectedJobId];
    storePartnerVisitsToLocal(state);
    return state;
  },
  setTravelTimes: (state, { payload: { times } }) => {
    const { selectedJobId, jobVisits } = state;
    if (!selectedJobId) return state;
    let jobVisit = jobVisits[selectedJobId];
    jobVisit.travelTimes = times.map((t) => ({
      ...t,
    }));
    const validationSchema = VisitValidationSchema(
      jobVisit.autoEndTime,
      jobVisit.followUp.followUpChecked,
    );
    const isValid = validationSchema.isValidSync(jobVisit);
    if (!isValid) {
      try {
        validationSchema.validateSync({ ...jobVisit }, { strict: true, abortEarly: false });
      } catch (e: any) {
        let { inner } = e as ValidationError;
        let errors: any = {};
        for (let error of inner) {
          if (!error.path) continue;
          errors[error.path] = error.message;
        }
        jobVisit.errors = { ...errors };
      }
    } else {
      jobVisit.errors = {};
    }
    jobVisit.lastUpdated = new Date().toISOString();
    storePartnerVisitsToLocal(state);
    return state;
  },
  setAutoEndTime: (state, { payload: { autoEndTime } }) => {
    if (!state.selectedJobId) return state;
    state.jobVisits[state.selectedJobId].autoEndTime = autoEndTime;
    state.jobVisits[state.selectedJobId].lastUpdated = new Date().toISOString();
    storePartnerVisitsToLocal(state);
    return state;
  },
  setWorkTimesInvoiceType: (state, { payload: { invoiceType } }) => {
    if (!state.selectedJobId) return state;
    state.jobVisits[state.selectedJobId].workTimesInvoiceType = invoiceType;
    state.jobVisits[state.selectedJobId].lastUpdated = new Date().toISOString();
    storePartnerVisitsToLocal(state);
    return state;
  },
  setTravelTimesInvoiceType: (state, { payload: { invoiceType } }) => {
    if (!state.selectedJobId) return state;
    state.jobVisits[state.selectedJobId].travelTimesInvoiceType = invoiceType;
    state.jobVisits[state.selectedJobId].lastUpdated = new Date().toISOString();
    storePartnerVisitsToLocal(state);
    return state;
  },
  // Form
  setVisitValue: (state, { payload: { key, value, shouldValidate = true } }) => {
    const { selectedJobId, jobVisits } = state;
    if (!selectedJobId || !jobVisits[selectedJobId]) {
      throw new Error("JobVisit not initialized or unavailable. Should not be possible to trigger");
    }
    const jobVisit = jobVisits[selectedJobId];
    jobVisit[key] = value;
    if (shouldValidate) {
      const validationSchema = VisitValidationSchema(
        jobVisit.autoEndTime,
        jobVisit.followUp.followUpChecked,
      );
      const isValid = validationSchema.isValidSync(jobVisit);
      if (!isValid) {
        try {
          validationSchema.validateSync({ ...jobVisit }, { strict: true, abortEarly: false });
        } catch (e: any) {
          let { inner } = e as ValidationError;
          let errors: any = {};
          for (let error of inner) {
            if (!error.path) continue;
            errors[error.path] = error.message;
          }
          jobVisit.errors = { ...errors };
        }
      } else {
        jobVisit.errors = {};
      }

      const signoffValidation = TechnicianSignoffValidationSchema();
      const isSignoffValid = signoffValidation.isValidSync({ ...jobVisit });
      if (!isSignoffValid) {
        try {
          signoffValidation.validateSync({ ...jobVisit }, { strict: true, abortEarly: false });
        } catch (e: any) {
          let { inner } = e as ValidationError;
          let errors: any = {};
          for (let error of inner) {
            if (!error.path) continue;
            errors[error.path] = error.message;
          }
          jobVisit.signoffErrors = { ...errors };
        }
      } else {
        jobVisit.signoffErrors = {};
      }
    }
    jobVisit.lastUpdated = new Date().toISOString();
    storePartnerVisitsToLocal(state);
    return state;
  },
  validateVisit: (state) => {
    const { selectedJobId, jobVisits } = state;
    if (!selectedJobId || !jobVisits[selectedJobId]) {
      throw new Error("JobVisit not initialized or unavailable. Should not be possible to trigger");
    }
    const jobVisit = jobVisits[selectedJobId];
    const validationSchema = VisitValidationSchema(
      jobVisit.autoEndTime,
      jobVisit.followUp.followUpChecked,
    );
    const isValid = validationSchema.isValidSync(jobVisit);
    if (!isValid) {
      try {
        validationSchema.validateSync({ ...jobVisit }, { strict: true, abortEarly: false });
      } catch (e: any) {
        let { inner } = e as ValidationError;
        let errors: any = {};
        for (let error of inner) {
          if (!error.path) continue;
          errors[error.path] = error.message;
        }
        jobVisit.errors = { ...errors };
      }
    } else {
      jobVisit.errors = {};
    }

    const signoffValidation = TechnicianSignoffValidationSchema();
    const isSignoffValid = signoffValidation.isValidSync({ ...jobVisit });
    if (!isSignoffValid) {
      try {
        signoffValidation.validateSync({ ...jobVisit }, { strict: true, abortEarly: false });
      } catch (e: any) {
        let { inner } = e as ValidationError;
        let errors: any = {};
        for (let error of inner) {
          if (!error.path) continue;
          errors[error.path] = error.message;
        }
        jobVisit.signoffErrors = { ...errors };
      }
    } else {
      jobVisit.signoffErrors = {};
    }
    jobVisit.lastUpdated = new Date().toISOString();
    storePartnerVisitsToLocal(state);
    return state;
  },
  // Checklists
  updateChecklist: (state, { payload: { checklist, index } }) => {
    const { selectedJobId, jobVisits } = state;
    if (!selectedJobId || !jobVisits[selectedJobId]) {
      throw new Error("JobVisit not initialized or unavailable. Should not be possible to trigger");
    }
    let { checklists } = jobVisits[selectedJobId];
    checklist.questions = checklist.questions.map((q) => {
      if (
        q.dataType === QuestionDataType.DateTime ||
        q.dataType === QuestionDataType.Date ||
        q.dataType === QuestionDataType.TimeOfDay
      ) {
        q.answer = toDateString(q.answer);
      }
      return q;
    });
    checklists[index] = {
      checklist,
      hasPreStart: checklist.questions.some((q) => q.isRequiredPreStart),
    };
    jobVisits[selectedJobId].checklists = [...checklists];
    jobVisits[selectedJobId].lastUpdated = new Date().toISOString();
    storePartnerVisitsToLocal(state);
    return state;
  },
  // Files
  addFile: (state, { payload: { file } }) => {
    const { selectedJobId, jobVisits } = state;
    if (!selectedJobId || !jobVisits[selectedJobId]) {
      throw new Error("JobVisit not initialized or unavailable. Should not be possible to trigger");
    }
    let { files } = jobVisits[selectedJobId];
    jobVisits[selectedJobId].files = [file, ...files];
    jobVisits[selectedJobId].lastUpdated = new Date().toISOString();
    storePartnerVisitsToLocal(state);
    return state;
  },
  removeFile: (state, { payload: { id } }) => {
    const { selectedJobId, jobVisits } = state;
    if (!selectedJobId || !jobVisits[selectedJobId]) {
      throw new Error("JobVisit not initialized or unavailable. Should not be possible to trigger");
    }
    let { files } = jobVisits[selectedJobId];
    files = files.filter((f) => f.id !== id);
    jobVisits[selectedJobId].files = [...files];
    jobVisits[selectedJobId].lastUpdated = new Date().toISOString();
    storePartnerVisitsToLocal(state);
    return state;
  },
  updateFile: (state, { payload: { file } }) => {
    const { selectedJobId, jobVisits } = state;
    if (!selectedJobId || !jobVisits[selectedJobId]) {
      throw new Error("JobVisit not initialized or unavailable. Should not be possible to trigger");
    }
    let { files } = jobVisits[selectedJobId];
    let index = files.findIndex((f) => f.id === file.id);
    files[index] = file;
    jobVisits[selectedJobId].files = [...files];
    jobVisits[selectedJobId].lastUpdated = new Date().toISOString();
    return state;
  },
  // MeterReadings
  updateMeterReadings: (state, { payload: { readings } }) => {
    const { selectedJobId, jobVisits } = state;
    if (!selectedJobId || !jobVisits[selectedJobId]) {
      throw new Error("JobVisit not initialized or unavailable. Should not be possible to trigger");
    }
    jobVisits[selectedJobId].meterReadings = [
      ...readings.map((m) => {
        if (m.currentReading === undefined || m.currentReading === null)
          return { typeId: m.typeId };
        else return m;
      }),
    ];
    jobVisits[selectedJobId].lastUpdated = new Date().toISOString();
    storePartnerVisitsToLocal(state);
    return state;
  },
  setMetersNotSaved: (state, { payload: { unSavedChanges } }) => {
    const { selectedJobId, jobVisits } = state;
    if (!selectedJobId || !jobVisits[selectedJobId]) {
      throw new Error("JobVisit not initialized or unavailable. Should not be possible to trigger");
    }
    jobVisits[selectedJobId].metersNotSaved = unSavedChanges;
    jobVisits[selectedJobId].lastUpdated = new Date().toISOString();
    storePartnerVisitsToLocal(state);
    return state;
  },
  setMetersUseCurrent: (state, { payload: { useCurrent } }) => {
    const { selectedJobId, jobVisits } = state;
    if (!selectedJobId || !jobVisits[selectedJobId]) {
      throw new Error("JobVisit not initialized or unavailable. Should not be possible to trigger");
    }
    jobVisits[selectedJobId].metersUseCurrent = useCurrent;
    jobVisits[selectedJobId].lastUpdated = new Date().toISOString();
    storePartnerVisitsToLocal(state);
    return state;
  },
  // Extras
  addExtra: (state, { payload: { extra } }) => {
    const { selectedJobId, jobVisits } = state;
    if (!selectedJobId || !jobVisits[selectedJobId]) {
      throw new Error("JobVisit not initialized or unavailable. Should not be possible to trigger");
    }
    let { extras } = jobVisits[selectedJobId];
    jobVisits[selectedJobId].extras = [extra, ...extras];
    jobVisits[selectedJobId].lastUpdated = new Date().toISOString();
    storePartnerVisitsToLocal(state);
    return state;
  },
  removeExtra: (state, { payload: { index } }) => {
    const { selectedJobId, jobVisits } = state;
    if (!selectedJobId || !jobVisits[selectedJobId]) {
      throw new Error("JobVisit not initialized or unavailable. Should not be possible to trigger");
    }
    let { extras } = jobVisits[selectedJobId];
    extras.splice(index, 1);
    jobVisits[selectedJobId].extras = [...extras];
    jobVisits[selectedJobId].lastUpdated = new Date().toISOString();
    storePartnerVisitsToLocal(state);
    return state;
  },
  updateExtra: (state, { payload: { extra, index } }) => {
    const { selectedJobId, jobVisits } = state;
    if (!selectedJobId || !jobVisits[selectedJobId]) {
      throw new Error("JobVisit not initialized or unavailable. Should not be possible to trigger");
    }
    let { extras } = jobVisits[selectedJobId];
    extras[index] = extra;
    jobVisits[selectedJobId].extras = [...extras];
    jobVisits[selectedJobId].lastUpdated = new Date().toISOString();
    storePartnerVisitsToLocal(state);
    return state;
  },
  // Parts
  addPart: (state, { payload: { part } }) => {
    const { selectedJobId, jobVisits } = state;
    if (!selectedJobId || !jobVisits[selectedJobId]) {
      throw new Error("JobVisit not initialized or unavailable. Should not be possible to trigger");
    }
    let { usedParts } = jobVisits[selectedJobId];
    jobVisits[selectedJobId].usedParts = [part, ...usedParts];
    if (part.part.status === PartStatus.Requested) {
      jobVisits[selectedJobId].followUp = {
        ...jobVisits[selectedJobId].followUp,
        followUpChecked: true,
      };
    } else if (!usedParts.some((part) => part.part.status === PartStatus.Requested)) {
      jobVisits[selectedJobId].followUp = {
        ...jobVisits[selectedJobId].followUp,
        followUpChecked: false,
      };
    }
    jobVisits[selectedJobId].lastUpdated = new Date().toISOString();
    storePartnerVisitsToLocal(state);
    return state;
  },
  addParts: (state, { payload: { parts } }) => {
    const { selectedJobId, jobVisits } = state;
    if (!selectedJobId || !jobVisits[selectedJobId]) {
      throw new Error("JobVisit not initialized or unavailable. Should not be possible to trigger");
    }
    let { usedParts } = jobVisits[selectedJobId];
    jobVisits[selectedJobId].usedParts = [...parts, ...usedParts];
    if (parts.some((part) => part.part.status === PartStatus.Requested)) {
      jobVisits[selectedJobId].followUp = {
        ...jobVisits[selectedJobId].followUp,
        followUpChecked: true,
      };
    } else if (!usedParts.some((part) => part.part.status === PartStatus.Requested)) {
      jobVisits[selectedJobId].followUp = {
        ...jobVisits[selectedJobId].followUp,
        followUpChecked: false,
      };
    }
    jobVisits[selectedJobId].lastUpdated = new Date().toISOString();
    storePartnerVisitsToLocal(state);
    return state;
  },
  removePart: (state, { payload: { articleNo, stockStore } }) => {
    const { selectedJobId, jobVisits } = state;
    if (!selectedJobId || !jobVisits[selectedJobId]) {
      throw new Error("JobVisit not initialized or unavailable. Should not be possible to trigger");
    }
    let { usedParts } = jobVisits[selectedJobId];
    usedParts = usedParts.filter(
      (p) => !(p.part.articleNo === articleNo && p.part.stockStore === stockStore),
    );
    jobVisits[selectedJobId].usedParts = [...usedParts];
    if (!usedParts.some((part) => part.part.status === PartStatus.Requested)) {
      jobVisits[selectedJobId].followUp = {
        ...jobVisits[selectedJobId].followUp,
        followUpChecked: false,
      };
    } else if (usedParts.some((part) => part.part.status === PartStatus.Requested)) {
      jobVisits[selectedJobId].followUp = {
        ...jobVisits[selectedJobId].followUp,
        followUpChecked: true,
      };
    }
    jobVisits[selectedJobId].lastUpdated = new Date().toISOString();
    storePartnerVisitsToLocal(state);
    return state;
  },
  updatePart: (state, { payload: { part } }) => {
    const { selectedJobId, jobVisits } = state;
    if (!selectedJobId || !jobVisits[selectedJobId]) {
      throw new Error("JobVisit not initialized or unavailable. Should not be possible to trigger");
    }
    let { usedParts } = jobVisits[selectedJobId];
    let index = usedParts.findIndex((p) => p.part.articleNo === part.part.articleNo);
    usedParts[index] = part;
    jobVisits[selectedJobId].usedParts = [...usedParts];
    jobVisits[selectedJobId].lastUpdated = new Date().toISOString();
    storePartnerVisitsToLocal(state);
    return state;
  },
  // Signature
  setSignatureCustomer: (state, { payload: { signatureData } }) => {
    const { selectedJobId, jobVisits } = state;
    if (!selectedJobId || !jobVisits[selectedJobId]) {
      throw new Error("JobVisit not initialized or unavailable. Should not be possible to trigger");
    }
    jobVisits[selectedJobId].signatureCustomer = signatureData;
    jobVisits[selectedJobId].lastUpdated = new Date().toISOString();
    storePartnerVisitsToLocal(state);
    return state;
  },
  setSignatureTechnician: (state, { payload: { signatureData } }) => {
    const { selectedJobId, jobVisits } = state;
    if (!selectedJobId || !jobVisits[selectedJobId]) {
      throw new Error("JobVisit not initialized or unavailable. Should not be possible to trigger");
    }
    jobVisits[selectedJobId].signatureEngineer = signatureData;
    jobVisits[selectedJobId].lastUpdated = new Date().toISOString();
    storePartnerVisitsToLocal(state);
    return state;
  },
  addTravel: (state) => {
    const { selectedJobId, jobVisits } = state;
    if (!selectedJobId || !jobVisits[selectedJobId]) {
      throw new Error("JobVisit not initialized or unavailable. Should not be possible to trigger");
    }
    const newTravelTime = {
      startDate: toDateString(Date.now()),
      startTime: toDateString(Date.now()),
      uploaded: false,
      pendingUpload: false,
    };
    if (jobVisits[selectedJobId].travelTimes.length === 0) {
      jobVisits[selectedJobId].travelTimes = cloneDeep([newTravelTime]);
    } else {
      jobVisits[selectedJobId].travelTimes = [
        ...jobVisits[selectedJobId].travelTimes,
        newTravelTime,
      ];
    }
    jobVisits[selectedJobId].lastUpdated = new Date().toISOString();
    storePartnerVisitsToLocal(state);
    return state;
  },
  stopTravel: (state) => {
    const { selectedJobId, jobVisits } = state;
    if (!selectedJobId || !jobVisits[selectedJobId]) {
      throw new Error("JobVisit not initialized or unavailable. Should not be possible to trigger");
    }
    const lastIndex = jobVisits[selectedJobId].travelTimes.length - 1;
    if (lastIndex !== -1) {
      jobVisits[selectedJobId].travelTimes[lastIndex].stopDate = toDateString(Date.now());
      jobVisits[selectedJobId].travelTimes[lastIndex].stopTime = toDateString(Date.now());
    }
    jobVisits[selectedJobId].lastUpdated = new Date().toISOString();
    storePartnerVisitsToLocal(state);
    return state;
  },
  resetRefreshNotifications(state, { payload: { key } }) {
    if (key === "symptomText") state.refreshJobNotifications.symptomText = false;
    if (key === "notes") state.refreshJobNotifications.internalNotes = false;
    if (key === "files") state.refreshJobNotifications.files = false;
    if (key === "parts") state.refreshJobNotifications.preOrderedParts = false;
    return state;
  },
};

// Definition of actual selectors
const selectors: Selectors = {
  selectPartnerBasicConsent({ partner: { partnerBasicConsent } }) {
    return partnerBasicConsent;
  },
  selectJobLoaded({ partner: { jobs }, root: { selectedJobId } }) {
    if (selectedJobId && jobs[selectedJobId]) {
      return true;
    }
    return false;
  },
  selectVisitLoaded({ partner: { selectedJobId, jobVisits } }) {
    if (!selectedJobId || !jobVisits[selectedJobId]) {
      return false;
    }
    return true;
  },
  selectTravelTimes({ partner: { jobs, jobVisits, selectedJobId } }) {
    if (!selectedJobId || !jobs[selectedJobId]) {
      throw new Error("SelectedJob unavailable. Should not be possible to trigger");
    }
    return jobVisits[selectedJobId].travelTimes;
  },
  selectSelectedJob({ partner: { jobs, selectedJobId } }) {
    if (!selectedJobId || !jobs[selectedJobId]) {
      throw new Error("SelectedJob unavailable. Should not be possible to trigger");
    }
    return jobs[selectedJobId];
  },
  selectSelectedJobVisit({ partner: { selectedJobId, jobVisits } }) {
    if (!selectedJobId || !jobVisits[selectedJobId]) {
      throw new Error("JobVisit not initialized or unavailable. Should not be possible to trigger");
    }
    return jobVisits[selectedJobId];
  },
  selectWorkNotes({ partner: { jobs, selectedJobId } }) {
    if (!selectedJobId || !jobs[selectedJobId]) {
      throw new Error("SelectedJob unavailable. Should not be possible to trigger");
    }
    return jobs[selectedJobId].workNotes;
  },
  selectTravelTabInvalid: ({ partner: { selectedJobId, jobVisits } }) => {
    if (!selectedJobId || !jobVisits[selectedJobId]) {
      throw new Error("SelectedJob unavailable. Should not be possible to trigger");
    }
    const visit = jobVisits[selectedJobId];

    // Actual validation issues will be caught via visit.errors check
    // but unmatched pairs do not validate correctly
    let travelHasMatchingStopTimes = true;
    if (visit.travelTimes.length > 0) {
      travelHasMatchingStopTimes = visit.travelTimes.every(({ stopDate, stopTime }) => {
        return !!stopDate && !!stopTime;
      });
    }
    return !travelHasMatchingStopTimes;
  },
  selectChecklistsIncomplete: ({ partner: { selectedJobId, jobVisits } }) => {
    if (!selectedJobId || !jobVisits[selectedJobId]) {
      throw new Error("SelectedJob unavailable. Should not be possible to trigger");
    }
    const visit = jobVisits[selectedJobId];

    let checklistsComplete = true;
    if (visit.checklists.length > 0) {
      checklistsComplete = visit.checklists.every(({ checklist }) => {
        const { isComplete } = getChecklistStatus(checklist);

        return isComplete;
      });
    }

    return !checklistsComplete;
  },
  selectVisitCanComplete: ({ partner: { selectedJobId, jobVisits } }) => {
    if (!selectedJobId || !jobVisits[selectedJobId]) {
      throw new Error("SelectedJob unavailable. Should not be possible to trigger");
    }
    const visit = jobVisits[selectedJobId];
    const isValid = !Object.keys(visit.errors).length;
    const meterReadingsComplete = !visit.metersNotSaved;

    let checklistsComplete = true;
    if (visit.checklists.length > 0) {
      checklistsComplete = visit.checklists.every(({ checklist }) => {
        const { isComplete } = getChecklistStatus(checklist);

        return isComplete;
      });
    }

    // Actual validation issues will be caught via visit.errors check
    // but unmatched pairs do not validate correctly
    let travelHasMatchingStopTimes = true;
    if (visit.travelTimes.length > 0) {
      travelHasMatchingStopTimes = visit.travelTimes.every(({ stopDate, stopTime }) => {
        return !!stopDate && !!stopTime;
      });
    }
    return isValid && checklistsComplete && meterReadingsComplete && travelHasMatchingStopTimes;
  },
  selectIsJobInProgress: ({ partner: { jobs, selectedJobId, jobVisits } }) => {
    if (!selectedJobId || !jobs[selectedJobId] || !jobVisits[selectedJobId]) {
      throw new Error("SelectedJob unavailable. Should not be possible to trigger");
    }
    const job = jobs[selectedJobId];
    const visit = jobVisits[selectedJobId];

    return (
      job.status === JobStatus.InProgress ||
      (!isEmpty(visit.workTimes) && visit.workTimes[0].startTime !== null) ||
      (!isEmpty(visit.travelTimes) && visit.travelTimes[0].startTime != null)
    );
  },
  selectVisitStarted: ({ partner: { jobs, selectedJobId, jobVisits } }) => {
    if (!selectedJobId || !jobs[selectedJobId] || !jobVisits[selectedJobId]) {
      throw new Error("SelectedJob unavailable. Should not be possible to trigger");
    }
    const job = jobs[selectedJobId];
    const visit = jobVisits[selectedJobId];

    return (
      job.status === JobStatus.InProgress ||
      (!isEmpty(visit.workTimes) && visit.workTimes[0].startTime !== null)
    );
  },
  selectContactsLoading({ partner: { contactsLoading } }) {
    return contactsLoading;
  },
};

// * job: Name of store with lowercase letters
export const storeBase = createSlice<State, Reducers>({
  name: "partner",
  initialState,
  reducers,
  extraReducers: (builder: ActionReducerMapBuilder<State>) => {
    queryBuilder(builder);
    mutationBuilder(builder);
  },
});

// To be imported and added in store/reducers
export default storeBase.reducer;
export const {
  partnerInit,
  updatePartnerBasicConsent,
  setPartnerToken,
  setJobTab,
  resetJobStatus,
  setJobStatus,
  setChangeJobMachineOpen,
  addExtra,
  addFile,
  addPart,
  addParts,
  removeExtra,
  removeFile,
  removePart,
  setTravelTimes,
  setVisitCompleted,
  setVisitValue,
  updateChecklist,
  updateExtra,
  updateFile,
  updateMeterReadings,
  setMetersNotSaved,
  setMetersUseCurrent,
  updatePart,
  validateVisit,
  setSignatureCustomer,
  setSignatureTechnician,
  setAutoEndTime,
  setWorkTimesInvoiceType,
  setTravelTimesInvoiceType,
  addTravel,
  stopTravel,
  resetRefreshNotifications,
} = storeBase.actions;
export const {
  selectPartnerBasicConsent,
  selectJobLoaded,
  selectVisitLoaded,
  selectTravelTimes,
  selectSelectedJob,
  selectSelectedJobVisit,
  selectWorkNotes,
  selectTravelTabInvalid,
  selectChecklistsIncomplete,
  selectVisitCanComplete,
  selectIsJobInProgress,
  selectVisitStarted,
  selectContactsLoading,
} = selectors;

export const { getPartnerJob, refreshJob, getFiles, getNotes, getContacts, getComments } =
  asyncQueries;

export const {
  loadPartnerJobVisitsInit,
  acceptJob,
  updateMachineLocation,
  updateMachineInstallDate,
  changeJobMachine,
  completeVisit,
  updateMachineCustomProps,
  updateMeters,
  updateVisit,
  uploadFile,
} = asyncMutations;
